/**
 * KontrollDetailScreen — one backlog element (Bereich 5, route
 * /portfolio/kontrolle/$stufe/$id). Left = the read-only element card; right = the Zuweisen
 * card.
 *
 * "‹ Zurück zur Kontrollliste" · left card ("{KANAL} · EINGEGANGEN AM {date}", betreff, sender,
 * "Stufe N · M Tage" pill, VERKNÜPFT MIT / FRISTENSTUFE SEIT, PRIORISIERUNGSSIGNALE, the grey
 * "setzt nicht auf bearbeitet" note) · right card = <ZuweisenPanel>. Gated on
 * administration.kontrolle.rueckstand.einsehen (api is the hard line).
 *
 * [Source: V4.0-inbox/cockpit-screenshots/image3,5,8,11.png]
 */

import { ChevronLeft } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { dateShortDe } from '../../lib/format';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { NoAccess, LoadingState, ErrorState } from '../administration/components';
import { useKontrolleDetail } from './hooks';
import { StufeStatusPill } from './components/StufePill';
import { SignalBadge } from './components/SignalBadge';
import { ZuweisenPanel } from './components/ZuweisenPanel';
import { KANAL_LABEL, KONTROLLE_EINSEHEN_KEY, parseStufe } from './constants';

export function KontrollDetailScreen() {
  const { stufe: stufeParam, id } = useParams({ strict: false }) as { stufe?: string; id?: string };
  const stufe = parseStufe(stufeParam);
  const navigate = useNavigate();
  const access = useEffectivePermissions();
  const canView = access.can(KONTROLLE_EINSEHEN_KEY);

  const detailQuery = useKontrolleDetail(id ?? '', canView && Boolean(id));
  const item = detailQuery.data;

  function backToListe() {
    void navigate({ to: '/portfolio/kontrolle/$stufe', params: { stufe: String(stufe ?? 1) } });
  }

  const back = (
    <button
      type="button"
      onClick={backToListe}
      className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" /> Zurück zur Kontrollliste
    </button>
  );

  if (!canView) {
    return (
      <div className="px-6 py-5">
        <NoAccess message="Für den Kontrollbereich fehlt Ihnen die Berechtigung." />
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="px-6 py-5">
        {back}
        <LoadingState />
      </div>
    );
  }

  if (detailQuery.isError || !item) {
    return (
      <div className="px-6 py-5">
        {back}
        <ErrorState />
      </div>
    );
  }

  return (
    <div className="px-6 py-5">
      {back}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left: the read-only element card */}
        <div className="rounded-xl border border-border bg-card p-5 text-card-foreground shadow-xs lg:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {KANAL_LABEL[item.kanal].toUpperCase()} · EINGEGANGEN AM {dateShortDe(item.eingegangen_am)}
              </div>
              <h1 className="mt-1.5 text-xl font-semibold text-foreground">{item.betreff}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">{item.absender}</p>
            </div>
            <StufeStatusPill stufe={item.stufe} alterTage={item.alter_tage} />
          </div>

          <div className="my-4 border-t border-hairline" />

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Verknüpft mit
              </div>
              <div className="mt-1 text-sm font-semibold text-foreground">
                {item.verknuepft_mit ?? '-'}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Fristenstufe seit
              </div>
              <div className="mt-1 text-sm font-semibold text-foreground">
                {dateShortDe(item.fristenstufe_seit)}
              </div>
            </div>
          </div>

          {item.signale.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Priorisierungssignale
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {item.signale.map((s) => (
                  <SignalBadge key={s.typ} typ={s.typ} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 rounded-lg bg-secondary/60 px-4 py-3 text-sm text-muted-foreground">
            Die Zuweisung setzt dieses Element nicht auf „bearbeitet“. Es bleibt im Kontrollbereich
            sichtbar, bis es effektiv gesichtet oder abgeschlossen wird. Jede Zuweisung wird im
            Audit-Log erfasst.
          </div>
        </div>

        {/* Right: Zuweisen */}
        <ZuweisenPanel itemId={item.id} stufe={item.stufe} />
      </div>
    </div>
  );
}
